import React, { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
    ChevronLeft,
    CheckCircle2,
    Wallet,
    ShieldCheck,
    Users,
    Calendar,
    Banknote,
} from "lucide-react";
import { useData } from "../context/DataContext";
import { useAuth } from "../context/AuthContext";
import { formatCurrency } from "../lib/format";
import PhoneInputField from "../components/PhoneInputField";

const Steps = ["Trip Details", "Passenger Info", "Confirm & Pay"];

const CabBooking = () => {
    const { cabId } = useParams();
    const [search] = useSearchParams();
    const { cabs, createBooking, settings } = useData();
    const { user } = useAuth();
    const navigate = useNavigate();

    const cab = cabs.find((c) => c.id === cabId);

    const [step, setStep] = useState(0);
    const [tripType, setTripType] = useState(search.get("tripType") || "One Way");
    const [from, setFrom] = useState(search.get("from") || "Cochin");
    const [to, setTo] = useState(search.get("to") || "Munnar");
    const [date, setDate] = useState(search.get("date") || "");
    const [returnDate, setReturnDate] = useState(search.get("returnDate") || "");
    const [passengers, setPassengers] = useState(Number(search.get("passengers") || 2));
    const [days, setDays] = useState(1);

    const [name, setName] = useState(user?.name || "");
    const [email, setEmail] = useState(user?.email || "");
    const [phone, setPhone] = useState(user?.phone || "");
    const [pickupAddress, setPickupAddress] = useState("");
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const totalAmount = useMemo(
        () => (cab?.ratePerDay || 0) * Math.max(1, days),
        [cab, days],
    );

    if (!cab) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <div className="text-center">
                    <p className="text-ink-light">Cab not found.</p>
                    <button onClick={() => navigate("/cab-booking")} className="mt-4 text-blue-500 font-semibold">Back to cabs</button>
                </div>
            </div>
        );
    }

    if (settings && settings.bookingsEnabled === false) {
        return (
            <div className="min-h-[60vh] max-w-md mx-auto px-4 py-20 text-center">
                <h1 className="font-display text-3xl text-ink">Bookings paused</h1>
                <p className="text-ink-light mt-2">
                    {settings.bannerMessage || "Online bookings are temporarily disabled. Please call us."}
                </p>
            </div>
        );
    }

    const next = () => setStep((s) => Math.min(2, s + 1));
    const prev = () => setStep((s) => Math.max(0, s - 1));

    const handleStep0 = (e) => {
        e.preventDefault();
        if (!date) return toast.error("Please choose a pickup date.");
        if (tripType === "Round Trip" && !returnDate)
            return toast.error("Please choose a return date.");
        next();
    };
    const handleStep1 = (e) => {
        e.preventDefault();
        if (!name || !email || !phone)
            return toast.error("Please complete all passenger details.");
        if (!/\S+@\S+\.\S+/.test(email))
            return toast.error("Enter a valid email address.");
        if (phone.replace(/\D/g, "").length < 10)
            return toast.error("Enter a valid phone number.");
        next();
    };

    const handleConfirm = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const created = await createBooking({
                type: "cab",
                cabId: cab.id,
                tripType,
                from,
                to,
                date,
                returnDate,
                days,
                passengers,
                customer: { name, email, phone, pickupAddress, notes },
            });
            toast.success("Booking confirmed! Confirmation email sent.");
            navigate(`/booking/confirmation/${created.id}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not create booking.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="bg-cream-100 min-h-screen pb-20" data-testid="cab-booking-page">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
                <button onClick={() => navigate(-1)} className="text-sm text-ink-light hover:text-ink flex items-center gap-1 mb-4">
                    <ChevronLeft className="w-4 h-4" /> Back
                </button>

                <div className="flex items-center justify-center gap-2 md:gap-4 mb-8">
                    {Steps.map((s, i) => (
                        <React.Fragment key={s}>
                            <div className={`flex items-center gap-2 ${i === step ? "text-blue-500" : i < step ? "text-blue-600" : "text-ink-light/60"}`}>
                                <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                                    i === step ? "bg-blue-500 text-white" : i < step ? "bg-blue-600 text-white" : "bg-white border border-cream-200"
                                }`}>
                                    {i < step ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                                </span>
                                <span className="hidden md:inline text-sm font-semibold">{s}</span>
                            </div>
                            {i < Steps.length - 1 && (
                                <span className={`w-8 md:w-16 h-0.5 ${i < step ? "bg-blue-600" : "bg-cream-200"}`} />
                            )}
                        </React.Fragment>
                    ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    <div className="lg:col-span-8 space-y-6">
                        {step === 0 && (
                            <form onSubmit={handleStep0} className="bg-white rounded-2xl border border-cream-200 p-6">
                                <h2 className="font-display text-2xl text-ink font-bold mb-4">Trip details</h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">Trip type</label>
                                        <select value={tripType} onChange={(e) => setTripType(e.target.value)} className="bare mt-1 w-full px-3 py-2.5 rounded-lg border border-cream-200 bg-white" data-testid="cb-trip-type">
                                            <option>One Way</option>
                                            <option>Round Trip</option>
                                            <option>Local / Hourly</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">No. of passengers</label>
                                        <input type="number" min={1} max={cab.seats} value={passengers} onChange={(e) => setPassengers(Number(e.target.value))} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-cream-200 bg-white" data-testid="cb-passengers" />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">From (Pickup city)</label>
                                        <input value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-cream-200 bg-white" data-testid="cb-from" />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">To (Drop city)</label>
                                        <input value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-cream-200 bg-white" data-testid="cb-to" />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">Pickup date</label>
                                        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-cream-200 bg-white" data-testid="cb-date" />
                                    </div>
                                    {tripType === "Round Trip" && (
                                        <div>
                                            <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">Return date</label>
                                            <input type="date" value={returnDate} onChange={(e) => setReturnDate(e.target.value)} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-cream-200 bg-white" data-testid="cb-return" />
                                        </div>
                                    )}
                                    <div>
                                        <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">No. of days</label>
                                        <input type="number" min={1} value={days} onChange={(e) => setDays(Number(e.target.value))} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-cream-200 bg-white" data-testid="cb-days" />
                                    </div>
                                </div>
                                <div className="flex justify-end mt-5">
                                    <button type="submit" className="px-7 py-3 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-semibold transition shadow-sm" data-testid="cb-step0-next">Continue</button>
                                </div>
                            </form>
                        )}

                        {step === 1 && (
                            <form onSubmit={handleStep1} className="bg-white rounded-2xl border border-cream-200 p-6">
                                <h2 className="font-display text-2xl text-ink font-bold mb-4">Passenger info</h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">Full name</label>
                                        <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-cream-200 bg-white" data-testid="cb-name" required />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">Email</label>
                                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-cream-200 bg-white" data-testid="cb-email" required />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">Phone</label>
                                        <PhoneInputField value={phone} onChange={setPhone} testId="cb-phone" required />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">Pickup address</label>
                                        <input value={pickupAddress} onChange={(e) => setPickupAddress(e.target.value)} placeholder="Hotel / address / landmark" className="mt-1 w-full px-3 py-2.5 rounded-lg border border-cream-200 bg-white" data-testid="cb-pickup" />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">Special notes (optional)</label>
                                        <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-cream-200 bg-white" data-testid="cb-notes" />
                                    </div>
                                </div>
                                <div className="flex justify-between mt-5">
                                    <button type="button" onClick={prev} className="px-5 py-3 rounded-xl border border-cream-200 text-ink font-semibold">Back</button>
                                    <button type="submit" className="px-7 py-3 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-semibold transition shadow-sm" data-testid="cb-step1-next">Continue</button>
                                </div>
                            </form>
                        )}

                        {step === 2 && (
                            <form onSubmit={handleConfirm} className="bg-white rounded-2xl border border-cream-200 p-6">
                                <h2 className="font-display text-2xl text-ink font-bold mb-2">Confirm your booking</h2>
                                <p className="text-sm text-ink-light mb-4">No prepayment required — pay the driver on arrival.</p>

                                <div className="rounded-xl border-2 border-blue-500 bg-blue-50 p-5 flex items-center gap-4" data-testid="pay-direct-driver">
                                    <span className="w-12 h-12 rounded-xl bg-blue-500 text-white flex items-center justify-center shrink-0">
                                        <Wallet className="w-6 h-6" />
                                    </span>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="font-semibold text-ink">Direct to Driver</span>
                                            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-500 text-white font-semibold">On arrival</span>
                                        </div>
                                        <div className="text-sm text-ink-light mt-1">Cash or UPI directly to your driver. Receipt on demand.</div>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2 mt-4 px-4 py-3 rounded-lg bg-cream-100 text-ink-light text-xs border border-cream-200">
                                    <ShieldCheck className="w-4 h-4 text-blue-500 shrink-0" />
                                    <span>Your booking is held without prepayment. We'll send a confirmation email and our team will reconfirm on WhatsApp before pickup.</span>
                                </div>

                                <div className="flex justify-between mt-5">
                                    <button type="button" onClick={prev} className="px-5 py-3 rounded-xl border border-cream-200 text-ink font-semibold">Back</button>
                                    <button type="submit" disabled={submitting} className="px-8 py-3 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-semibold transition shadow-md brand-shadow disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2" data-testid="cb-confirm-pay">
                                        <Banknote className="w-4 h-4" />
                                        {submitting ? "Booking…" : `Confirm booking · ${formatCurrency(totalAmount)}`}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>

                    <aside className="lg:col-span-4">
                        <div className="bg-white rounded-2xl border border-cream-200 overflow-hidden sticky top-24">
                            <img src={cab.image} alt={cab.name} className="w-full h-40 object-cover" />
                            <div className="p-5">
                                <div className="text-xs font-semibold uppercase tracking-wider text-blue-500">{cab.category}</div>
                                <h3 className="font-display text-2xl text-ink font-bold">{cab.name}</h3>
                                <div className="text-xs text-ink-light flex items-center gap-3 mt-1">
                                    <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {cab.seats}</span>
                                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {cab.kmLimit} km/day</span>
                                </div>

                                <dl className="mt-4 space-y-2 text-sm pt-4 border-t border-cream-200">
                                    <div className="flex justify-between"><dt className="text-ink-light">Trip</dt><dd className="font-medium text-ink">{tripType}</dd></div>
                                    <div className="flex justify-between"><dt className="text-ink-light">Route</dt><dd className="font-medium text-ink truncate ml-2">{from} → {to}</dd></div>
                                    <div className="flex justify-between"><dt className="text-ink-light">Days</dt><dd className="font-medium text-ink">{days}</dd></div>
                                    <div className="flex justify-between"><dt className="text-ink-light">Per day</dt><dd className="font-medium text-ink">{formatCurrency(cab.ratePerDay)}</dd></div>
                                </dl>

                                <div className="mt-4 pt-4 border-t border-cream-200 flex items-center justify-between">
                                    <span className="text-ink-light text-sm">Total</span>
                                    <span className="font-display text-2xl font-bold text-blue-500" data-testid="cb-total">{formatCurrency(totalAmount)}</span>
                                </div>
                                <div className="text-[11px] text-ink-light mt-1 text-right">Payable to driver on arrival</div>
                            </div>
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    );
};

export default CabBooking;
